import React from 'react';
import { ClockIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';

export type PdfPendingVariant = 'checking' | 'ready-later' | 'offline';

interface PdfPendingNoticeProps {
  /**
   * checking — a render-poll is actively re-requesting; no retry button.
   * ready-later — the poll gave up while the server was still rendering.
   * offline — the device lost connectivity before the PDF was downloaded.
   */
  variant: PdfPendingVariant;
  /** Re-runs the fetch. Shown for 'ready-later' and 'offline'. */
  onRetry: () => void;
  /** Optional extra actions rendered next to the retry button (e.g. Go Back). */
  children?: React.ReactNode;
}

const COPY: Record<PdfPendingVariant, { title: string; body: string }> = {
  checking: {
    title: 'PDF Still Being Prepared',
    body: 'This PDF is being generated. Checking for it now…',
  },
  'ready-later': {
    title: 'PDF Still Being Prepared',
    body: 'This PDF is being generated and should be ready shortly.',
  },
  offline: {
    title: 'PDF Not Available Offline',
    body: "You're offline and this PDF hasn't been downloaded yet. It will be available once you're back online.",
  },
};

/**
 * Neutral "the PDF isn't ready yet" panel for the PDF view. A pending render
 * (or being offline before the download) is a normal transient state, so this
 * deliberately avoids failure styling.
 */
const PdfPendingNotice: React.FC<PdfPendingNoticeProps> = ({ variant, onRetry, children }) => {
  const Icon = variant === 'offline' ? SignalSlashIcon : ClockIcon;
  const { title, body } = COPY[variant];

  return (
    <div className="flex items-center justify-center h-full p-4" data-pdf-pending={variant}>
      <div className="text-center max-w-md">
        <Icon className="mx-auto h-12 w-12 text-blue-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-4">{body}</p>
        <div className="flex gap-2 justify-center">
          {variant !== 'checking' && (
            <Button variant="primary" onClick={onRetry}>
              Check Again
            </Button>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default PdfPendingNotice;
